// src/components/About/About.jsx
import React from "react";
import "./About.css";
import Banner from "../Banner/Banner";

const About = () => {
  return (
    <section className="aboutv3" aria-label="About us">
      <div className="av3-wrap">
        {/* Intro */}
        <header className="av3-head">
          <p className="av3-kicker">About us</p>
          <h1 className="av3-title">
            We're a small team that builds websites and marketing systems that
            actually generate leads.
          </h1>
          <p className="av3-sub">
            Design, development, SEO, paid, and automation — delivered as one clean
            system. No fluff, no chaos, just measurable progress.
          </p>
        </header>

        {/* Hero split */}
        <section className="av3-hero">
          <div className="av3-heroLeft">
            <h2 className="av3-h2">What we do (and how we work)</h2>
            <p className="av3-p">
              Most businesses don't need "more marketing". They need a reliable growth
              foundation: fast pages, clear messaging, tracking that works, and a funnel
              that converts. We help you set that up — then iterate based on data.
            </p>

            <div className="av3-pillGrid" role="list">
              <article className="av3-pill" role="listitem">
                <span className="av3-pillLabel">Best fit for</span>
                <ul>
                  <li>Local & service businesses that want steady leads</li>
                  <li>Founders who need a clean website + funnel</li>
                  <li>Teams tired of slow, messy vendor handoffs</li>
                </ul>
              </article>

              <article className="av3-pill" role="listitem">
                <span className="av3-pillLabel">What you get</span>
                <ul>
                  <li>Modern UI + performance-first builds</li>
                  <li>SEO structure + tracking baked in</li>
                  <li>Clear deliverables with weekly updates</li>
                </ul>
              </article>
            </div>
          </div>

          <aside className="av3-heroRight" aria-label="Work snapshot">
            <div className="av3-badge">
              <span className="av3-badgeDot" aria-hidden="true" />
              <span>One partner for website + growth</span>
            </div>

            <div className="av3-card">
              <h3>How working with us feels</h3>
              <ul>
                <li>Quick decisions with clear options</li>
                <li>Fast delivery with clean components</li>
                <li>Plain-English updates (no jargon)</li>
                <li>Transparent scope and priority list</li>
              </ul>

              <div className="av3-miniStats" role="list">
                <div className="mini" role="listitem">
                  <span className="k">7–21 days</span>
                  <span className="d">to first live improvements</span>
                </div>
                <div className="mini" role="listitem">
                  <span className="k">2–4 hrs</span>
                  <span className="d">avg. first response time</span>
                </div>
              </div>
            </div>
          </aside>
        </section>

        {/* Principles */}
        <section className="av3-section">
          <h2 className="av3-h2">Our principles</h2>
          <p className="av3-lead">
            We optimise for outcomes — not deliverables. Every recommendation is guided
            by these rules.
          </p>

          <div className="av3-values">
            {[
              {
                icon: "🧭",
                title: "Clarity before execution",
                text: "We align goals, scope, and priorities first — so the build stays clean and fast.",
              },
              {
                icon: "⚡",
                title: "Speed + performance",
                text: "Fast pages, clean structure, and mobile-first UX — because performance is conversion.",
              },
              {
                icon: "📈",
                title: "Measurable growth",
                text: "Tracking, events, and funnel visibility so decisions are based on data, not guesswork.",
              },
              {
                icon: "🤝",
                title: "Support like a partner",
                text: "You get updates, guidance, and improvements — not a 'handoff and vanish' experience.",
              },
            ].map((v) => (
              <article className="av3-value" key={v.title}>
                <div className="av3-ico" aria-hidden="true">
                  {v.icon}
                </div>
                <h3>{v.title}</h3>
                <p>{v.text}</p>
              </article>
            ))}
          </div>
        </section>

        {/* Numbers */}
        <section className="av3-metrics" aria-label="By the numbers">
          {[
            { k: "100+", d: "Pages shipped across websites & funnels" },
            { k: "30+", d: "Projects delivered across industries" },
            { k: "4.8/5", d: "Avg. client satisfaction rating" },
            { k: "< 24h", d: "Typical response time on working days" },
          ].map((s) => (
            <div className="metric" key={s.d}>
              <div className="k">{s.k}</div>
              <div className="d">{s.d}</div>
            </div>
          ))}
        </section>

        {/* Timeline */}
        <section className="av3-section">
          <h2 className="av3-h2">How we deliver</h2>
          <p className="av3-lead">
            A simple process that keeps momentum high and rework low.
          </p>

          <ol className="av3-steps">
            {[
              {
                t: "Discover",
                p: "We understand your offer, audience, and current funnel — then define success metrics.",
              },
              {
                t: "Plan",
                p: "We map pages, content, tracking, and priorities so execution is smooth and predictable.",
              },
              {
                t: "Build",
                p: "Design + development with performance and SEO structure baked in from day one.",
              },
              {
                t: "Launch & iterate",
                p: "Ship, validate tracking, then optimise based on leads, conversion rate, and user behavior.",
              },
            ].map((s) => (
              <li key={s.t}>
                <span className="pin" aria-hidden="true" />
                <h3>{s.t}</h3>
                <p>{s.p}</p>
              </li>
            ))}
          </ol>
        </section>

        {/* Quote */}
        <figure className="av3-quote">
          <blockquote>
            "The site finally feels modern, fast, and clear — and we started getting better
            inquiries within weeks."
          </blockquote>
          <figcaption>— Client feedback</figcaption>
        </figure>

        {/* CTA cards */}
        <section className="av3-cta" aria-label="Next steps">
          <div className="ctaCard">
            <h3>Want a quick growth plan?</h3>
            <p>
              Share your website + goals. We'll suggest 2–3 concrete moves for the next
              30 days.
            </p>
            <a className="btn btn-primary" href="/contact">
              Book a strategy call
            </a>
          </div>

          <div className="ctaCard">
            <h3>Prefer to browse first?</h3>
            <p>
              Explore services across design, development, SEO, paid marketing, and local
              growth.
            </p>
            <a className="btn btn-ghost" href="/services">
              Browse services
            </a>
          </div>
        </section>
      </div>

      <Banner />
    </section>
  );
};

export default About;
